import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Clock, MapPin, Phone, ChefHat, Truck, CheckCircle, XCircle, AlertCircle, User, Star, Receipt, Home, ShoppingCart } from 'lucide-react';
import { api } from '../config/api';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix default marker icon for Leaflet with webpack/CRA
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const driverIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const STATUS_FLOW = ['pending', 'confirmed', 'preparing', 'ready', 'out_for_delivery', 'delivered'];
const STATUS_LABELS = {
  pending: 'Order Received', confirmed: 'Order Confirmed', preparing: 'Preparing',
  ready: 'Ready for Delivery', out_for_delivery: 'Out for Delivery', delivered: 'Delivered',
  cancelled: 'Cancelled', rejected: 'Rejected'
};
const STATUS_DESCRIPTIONS = {
  pending: 'Your restaurant has received your order.',
  confirmed: 'Your food is being prepared.',
  preparing: 'Chef is preparing your food.',
  ready: 'Your order is ready for delivery.',
  out_for_delivery: 'Your food is on the way.',
  delivered: 'Delivered successfully.',
  cancelled: 'This order was cancelled.',
  rejected: 'This order was rejected.'
};
const STATUS_COLORS = {
  pending: '#FFC107', confirmed: '#2196F3', preparing: '#FF9800', ready: '#9C27B0',
  out_for_delivery: '#00BCD4', delivered: '#4CAF50', cancelled: '#F44336', rejected: '#F44336'
};

const OrderTracking = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [serverNow, setServerNow] = useState(Date.now());
  const pollRef = useRef(null);

  // Server-synced polling: fetch order + server time every 3 seconds
  useEffect(() => {
    fetchOrder();
    pollRef.current = setInterval(fetchOrder, 3000);
    return () => clearInterval(pollRef.current);
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      const res = await api.get(`/orders.php?order_id=${orderId}`);
      if (res.data.success) {
        setOrder(res.data.data);
        // Use server time if available, fallback to client time
        if (res.data.server_now) {
          setServerNow(new Date(res.data.server_now).getTime());
        }
      }
    } catch (err) {
      console.error('Failed to fetch order:', err);
    } finally {
      setLoading(false);
    }
  };

  const currentStatusIndex = STATUS_FLOW.indexOf(order?.status);
  const isCancelled = order?.status === 'cancelled' || order?.status === 'rejected';
  const isDelivered = order?.status === 'delivered';

  const formatDuration = (min, sec) => {
    if (min === 0 && sec === 0) return '0 seconds';
    if (min === 0) return `${sec} second${sec !== 1 ? 's' : ''}`;
    if (sec === 0) return `${min} minute${min !== 1 ? 's' : ''}`;
    return `${min} min ${sec} sec`;
  };

  const stageDurations = useMemo(() => {
    if (!order?.history || order.history.length === 0) return [];
    
    const stages = [];
    const statusLabels = {
      pending: 'Waiting for Confirmation',
      confirmed: 'Waiting for Confirmation',
      preparing: 'Preparing Food',
      ready: 'Ready for Delivery',
      out_for_delivery: 'Delivery Transit',
      delivered: 'Delivery Confirmed'
    };
    
    for (let i = 0; i < order.history.length - 1; i++) {
      const current = order.history[i];
      const next = order.history[i + 1];
      const startTime = new Date(current.changed_at).getTime();
      const endTime = new Date(next.changed_at).getTime();
      const elapsedMs = endTime - startTime;
      const elapsedMin = Math.floor(elapsedMs / 60000);
      const elapsedSec = Math.floor((elapsedMs % 60000) / 1000);
      
      if (elapsedMin > 0 || elapsedSec > 0) {
        stages.push({
          label: statusLabels[current.new_status] || current.new_status,
          minutes: elapsedMin,
          seconds: elapsedSec,
        });
      }
    }
    
    return stages;
  }, [order?.history]);

  // Per-stage remaining time calculation using server-synced timestamps
  const stageRemaining = useMemo(() => {
    if (!order || isDelivered || isCancelled) return null;

    const now = serverNow;
    let targetTime = null;

    if (order.status === 'confirmed' || order.status === 'preparing' || order.status === 'ready') {
      // Preparation stage: countdown from confirmed_at + prep_minutes
      if (order.confirmed_at) {
        targetTime = new Date(order.confirmed_at).getTime() + (order.prep_minutes || 15) * 60000;
      }
    }

    if (order.status === 'out_for_delivery') {
      // Delivery stage: countdown from updated_at (when status changed) + delivery_minutes
      if (order.updated_at) {
        targetTime = new Date(order.updated_at).getTime() + (order.delivery_minutes || 30) * 60000;
      }
    }

    if (!targetTime) return null;

    const remaining = targetTime - now;
    return Math.max(0, remaining);
  }, [order, serverNow, isDelivered, isCancelled]);

  const formatTime = (ms) => {
    if (ms === null || ms <= 0) return '00:00';
    const totalSec = Math.floor(ms / 1000);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const getEstimatedReadyTime = () => {
    if (!order?.confirmed_at || !order?.prep_minutes) return null;
    const ready = new Date(new Date(order.confirmed_at).getTime() + (order.prep_minutes || 15) * 60000);
    return ready.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const getEstimatedDeliveryTime = () => {
    if (!order?.confirmed_at || !order?.prep_minutes || !order?.delivery_minutes) return null;
    const ready = new Date(new Date(order.confirmed_at).getTime() + (order.prep_minutes || 15) * 60000);
    const delivery = new Date(ready.getTime() + (order.delivery_minutes || 30) * 60000);
    return delivery.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const getTotalEstimatedTime = () => {
    if (!order?.prep_minutes || !order?.delivery_minutes) return null;
    return (order.prep_minutes || 15) + (order.delivery_minutes || 30);
  };

  if (loading) {
    return (
      <div style={{ backgroundColor: '#faf8f5', minHeight: '100vh', paddingTop: '100px', paddingBottom: '60px' }}>
        <div className="container">
          <div style={{ textAlign: 'center', padding: '80px 0', color: '#999' }}>
            <div style={{ width: '36px', height: '36px', border: '3px solid #f0f0f0', borderTopColor: '#D4AF37', borderRadius: '50%', margin: '0 auto 16px', animation: 'spin 0.8s linear infinite' }}></div>
            <p>Loading order details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div style={{ backgroundColor: '#faf8f5', minHeight: '100vh', paddingTop: '100px', paddingBottom: '60px' }}>
        <div className="container">
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <XCircle size={48} color="#ddd" style={{ marginBottom: '16px' }} />
            <h4 style={{ fontFamily: 'Playfair Display, serif', color: '#999', marginBottom: '8px' }}>Order not found</h4>
            <Link to="/my-orders" className="btn btn-gold">Back to My Orders</Link>
          </div>
        </div>
      </div>
    );
  }

  // Completion screen
  if (isDelivered) {
    const orderTime = new Date(order.order_date);
    const deliveredTime = order.delivered_at ? new Date(order.delivered_at) : new Date(order.updated_at);
    const totalMs = deliveredTime.getTime() - orderTime.getTime();
    const totalMinutes = Math.floor(totalMs / 60000);
    const totalSeconds = Math.floor((totalMs % 60000) / 1000);

    return (
      <div style={{ backgroundColor: '#faf8f5', minHeight: '100vh', paddingTop: '100px', paddingBottom: '60px' }}>
        <div className="container" style={{ maxWidth: '700px' }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '20px', padding: '40px', textAlign: 'center', border: '1px solid #EAEDF1', boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
            <div style={{ width: '80px', height: '80px', borderRadius: '50%', backgroundColor: '#E8F5E9', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
              <CheckCircle size={48} color="#4CAF50" />
            </div>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '2rem', fontWeight: 700, color: '#1A1A1A', marginBottom: '8px' }}>
              🎉 Order Delivered Successfully!
            </h2>
            <p style={{ fontSize: '1.05rem', color: '#666', marginBottom: '32px' }}>
              Thank you for choosing <strong>Syra Food Point</strong>! We hope you enjoyed your meal.
            </p>

            <div style={{ backgroundColor: '#FAFAFA', borderRadius: '16px', padding: '24px', marginBottom: '24px', textAlign: 'left' }}>
              <h4 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 600, fontSize: '1.1rem', color: '#1A1A1A', marginBottom: '16px' }}>Order Summary</h4>
              {[
                { label: 'Order Placed', value: orderTime.toLocaleString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', day: 'numeric', month: 'short' }) },
                { label: 'Delivered At', value: deliveredTime.toLocaleString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', day: 'numeric', month: 'short' }) },
                { label: 'Actual Total Delivery Time', value: `${totalMinutes} min ${totalSeconds} sec`, highlight: true },
                { label: 'Order Total', value: `UGX ${Number(order.total_amount).toLocaleString()}`, highlight: true },
                { label: 'Payment Method', value: order.payment_method },
                { label: 'Order #', value: `#${order.order_id}` },
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: i < 5 ? '1px solid #f0f0f0' : 'none' }}>
                  <span style={{ fontSize: '0.88rem', color: '#666' }}>{item.label}</span>
                  <span style={{ fontSize: '0.88rem', fontWeight: item.highlight ? 700 : 500, color: item.highlight ? '#D4AF37' : '#333' }}>{item.value}</span>
                </div>
              ))}
            </div>

            {stageDurations.length > 0 && (
              <div style={{ backgroundColor: '#FAFAFA', borderRadius: '16px', padding: '24px', marginBottom: '24px', textAlign: 'left' }}>
                <h4 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 600, fontSize: '1.1rem', color: '#1A1A1A', marginBottom: '16px' }}>Delivery Timeline</h4>
                {stageDurations.map((stage, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: i < stageDurations.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                    <span style={{ fontSize: '0.85rem', color: '#666' }}>{stage.label}</span>
                    <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#333' }}>{formatDuration(stage.minutes, stage.seconds)}</span>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <Link to={`/review/${order.order_id}`} className="btn btn-gold" style={{ padding: '14px 24px', fontSize: '0.9rem', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <Star size={16} /> Rate Your Experience
              </Link>
              <Link to={`/receipt/${order.order_id}`} className="btn btn-outline-gold" style={{ padding: '14px 24px', fontSize: '0.9rem', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <Receipt size={16} /> View Receipt
              </Link>
              <Link to="/menu" className="btn btn-outline-gold" style={{ padding: '14px 24px', fontSize: '0.9rem', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <ShoppingCart size={16} /> Order Again
              </Link>
              <Link to="/" className="btn btn-outline-gold" style={{ padding: '14px 24px', fontSize: '0.9rem', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <Home size={16} /> Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#faf8f5', minHeight: '100vh', paddingTop: '100px', paddingBottom: '60px' }}>
      <div className="container" style={{ maxWidth: '800px' }}>
        <Link to="/my-orders" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#666', textDecoration: 'none', fontSize: '0.85rem', marginBottom: '20px' }}>
          <ArrowLeft size={16} /> Back to My Orders
        </Link>

        {/* Status header */}
        <div style={{ backgroundColor: '#fff', borderRadius: '16px', padding: '30px', marginBottom: '20px', border: '1px solid #EAEDF1', textAlign: 'center' }}>
          <div style={{ width: '64px', height: '64px', borderRadius: '50%', backgroundColor: STATUS_COLORS[order.status] + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            {isDelivered ? <CheckCircle size={32} color={STATUS_COLORS[order.status]} /> : isCancelled ? <XCircle size={32} color={STATUS_COLORS[order.status]} /> : <Clock size={32} color={STATUS_COLORS[order.status]} />}
          </div>
          <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.8rem', fontWeight: 600, color: '#1A1A1A', marginBottom: '8px' }}>
            {STATUS_LABELS[order.status] || order.status}
          </h2>
          <p style={{ color: '#666', fontSize: '1rem', maxWidth: '500px', margin: '0 auto 20px' }}>
            {STATUS_DESCRIPTIONS[order.status] || ''}
          </p>

          {stageRemaining !== null && !isDelivered && !isCancelled && (
            <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: '4px', padding: '16px 32px', backgroundColor: '#FAFAFA', borderRadius: '12px', marginTop: '16px' }}>
              <span style={{ fontSize: '0.75rem', color: '#999', textTransform: 'uppercase', letterSpacing: '1px' }}>Time Remaining</span>
              <span style={{ fontSize: '2.5rem', fontWeight: 700, color: '#1A1A1A', fontFamily: 'monospace', letterSpacing: '2px' }}>{formatTime(stageRemaining)}</span>
              <span style={{ fontSize: '0.75rem', color: '#999' }}>
                {order.status === 'out_for_delivery' ? `${order.delivery_minutes || 30} min delivery` : `${order.prep_minutes || 15} min prep`}
              </span>
            </div>
          )}

          {getEstimatedReadyTime() && !isDelivered && !isCancelled && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '32px', marginTop: '20px', flexWrap: 'wrap' }}>
              <div style={{ textAlign: 'center' }}>
                <span style={{ fontSize: '0.72rem', color: '#999', textTransform: 'uppercase', letterSpacing: '1px', display: 'block', marginBottom: '4px' }}>Estimated Ready</span>
                <span style={{ fontSize: '1.1rem', fontWeight: 600, color: '#333' }}>{getEstimatedReadyTime()}</span>
              </div>
              {getEstimatedDeliveryTime() && (
                <div style={{ textAlign: 'center' }}>
                  <span style={{ fontSize: '0.72rem', color: '#999', textTransform: 'uppercase', letterSpacing: '1px', display: 'block', marginBottom: '4px' }}>Estimated Delivery</span>
                  <span style={{ fontSize: '1.1rem', fontWeight: 600, color: '#333' }}>{getEstimatedDeliveryTime()}</span>
                </div>
              )}
              {getTotalEstimatedTime() && (
                <div style={{ textAlign: 'center' }}>
                  <span style={{ fontSize: '0.72rem', color: '#999', textTransform: 'uppercase', letterSpacing: '1px', display: 'block', marginBottom: '4px' }}>Total Time</span>
                  <span style={{ fontSize: '1.1rem', fontWeight: 600, color: '#333' }}>{getTotalEstimatedTime()} min</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Progress tracker */}
        {!isDelivered && !isCancelled && (
          <div style={{ backgroundColor: '#fff', borderRadius: '16px', padding: '30px', marginBottom: '20px', border: '1px solid #EAEDF1' }}>
            <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.2rem', fontWeight: 600, color: '#1A1A1A', marginBottom: '24px', textAlign: 'center' }}>
              Order Progress
            </h3>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', position: 'relative', padding: '0 8px' }}>
              <div style={{ position: 'absolute', top: '14px', left: '24px', right: '24px', height: '3px', backgroundColor: '#E0E0E0', zIndex: 0 }} />
              <div style={{ position: 'absolute', top: '14px', left: '24px', height: '3px', backgroundColor: STATUS_COLORS[order.status] || '#999', zIndex: 0, width: `${currentStatusIndex >= 0 ? (currentStatusIndex / (STATUS_FLOW.length - 1)) * 100 : 0}%`, transition: 'width 0.5s ease' }} />
              {STATUS_FLOW.map((s, i) => {
                const isActive = i <= currentStatusIndex;
                const isCurrent = i === currentStatusIndex;
                return (
                  <div key={s} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', flex: 1, position: 'relative', zIndex: 1 }}>
                    <div style={{
                      width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      backgroundColor: isActive ? STATUS_COLORS[s] : '#fff',
                      border: `3px solid ${isActive ? STATUS_COLORS[s] : '#E0E0E0'}`,
                      color: isActive ? '#fff' : '#999',
                      fontSize: '0.75rem', fontWeight: 700, transition: 'all 0.3s'
                    }}>
                      {isActive ? '✓' : i + 1}
                    </div>
                    <span style={{ fontSize: '0.7rem', fontWeight: isCurrent ? 700 : 500, color: isActive ? '#333' : '#999', textAlign: 'center', whiteSpace: 'nowrap' }}>
                      {STATUS_LABELS[s]}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Driver info */}
        {['out_for_delivery', 'arriving_soon', 'arrived', 'driving_to_restaurant', 'arrived_at_restaurant', 'picked_up'].includes(order.status) && (order.driver_name || order.driver_phone || order.vehicle_type) && (
          <div style={{ backgroundColor: '#fff', borderRadius: '16px', padding: '24px', marginBottom: '20px', border: '1px solid #EAEDF1', boxShadow: '0 4px 16px rgba(0,0,0,0.04)' }}>
            <h4 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 600, fontSize: '1.1rem', color: '#1565C0', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Truck size={18} />
              Your Driver
            </h4>

            {/* Driver Profile Card */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px', padding: '16px', backgroundColor: '#F8F9FF', borderRadius: '12px', border: '1px solid #E8ECFF' }}>
              <div style={{ width: '64px', height: '64px', borderRadius: '50%', backgroundColor: '#E3F2FD', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0, border: '3px solid #BBDEFB' }}>
                {order.driver_photo ? (
                  <img src={order.driver_photo} alt="Driver" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <User size={28} color="#1565C0" />
                )}
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ fontWeight: 700, fontSize: '1rem', color: '#1A1A1A', margin: '0 0 4px' }}>{order.driver_name || 'Your Driver'}</p>
                {order.vehicle_type && (
                  <p style={{ fontSize: '0.83rem', color: '#666', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Truck size={13} />
                    {order.vehicle_type}{order.driver_reg ? ` · ${order.driver_reg}` : ''}
                  </p>
                )}
                {order.driver_phone && (
                  <a href={`tel:${order.driver_phone}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '0.83rem', color: '#1565C0', fontWeight: 600, textDecoration: 'none' }}>
                    <Phone size={13} />
                    {order.driver_phone}
                  </a>
                )}
              </div>
            </div>

            {/* Countdown */}
            {stageRemaining !== null && order.status === 'out_for_delivery' && (
              <div style={{ textAlign: 'center', padding: '16px', backgroundColor: '#E3F2FD', borderRadius: '12px', marginBottom: '16px' }}>
                <span style={{ fontSize: '0.72rem', color: '#1565C0', textTransform: 'uppercase', letterSpacing: '1px', display: 'block', marginBottom: '4px' }}>Estimated Arrival</span>
                <span style={{ fontSize: '2rem', fontWeight: 700, color: '#1565C0', fontFamily: 'monospace' }}>{formatTime(stageRemaining)}</span>
                <span style={{ fontSize: '0.8rem', color: '#666', display: 'block', marginTop: '4px' }}>{order.delivery_minutes || 30} min to reach</span>
              </div>
            )}

            {/* Live Driver Map */}
            {order.driver_lat && order.driver_lng && (
              <div style={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid #EAEDF1', marginBottom: '8px' }}>
                <div style={{ padding: '8px 12px', backgroundColor: '#F8F9FF', fontSize: '0.75rem', fontWeight: 600, color: '#666', borderBottom: '1px solid #EAEDF1', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <MapPin size={13} color="#D4AF37" />
                  Live Driver Location (updates every 30s)
                </div>
                <MapContainer
                  center={[parseFloat(order.driver_lat), parseFloat(order.driver_lng)]}
                  zoom={14}
                  style={{ height: '220px', width: '100%' }}
                  key={`${order.driver_lat}-${order.driver_lng}`}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  />
                  <Marker position={[parseFloat(order.driver_lat), parseFloat(order.driver_lng)]} icon={driverIcon}>
                    <Popup>
                      <strong>{order.driver_name || 'Driver'}</strong><br />
                      {order.vehicle_type || 'Delivery vehicle'}
                    </Popup>
                  </Marker>
                </MapContainer>
              </div>
            )}
          </div>
        )}

        {/* Delay notice */}
        {order.delay_reason && !isDelivered && !isCancelled && (
          <div style={{ backgroundColor: '#FFF3E0', borderRadius: '16px', padding: '20px', marginBottom: '20px', border: '1px solid #FFE0B2', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <AlertCircle size={20} color="#FF9800" style={{ marginTop: '2px' }} />
            <div>
              <h5 style={{ fontWeight: 600, color: '#E65100', marginBottom: '4px', fontSize: '0.9rem' }}>Delay Notice</h5>
              <p style={{ fontSize: '0.85rem', color: '#666', margin: 0 }}>{order.delay_reason}</p>
            </div>
          </div>
        )}

        {/* Delivery confirmation */}
        {order.status === 'out_for_delivery' && !confirmed && (
          <div style={{ backgroundColor: '#E8F5E9', borderRadius: '16px', padding: '24px', marginBottom: '20px', border: '1px solid #C8E6C9', textAlign: 'center' }}>
            <h4 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 600, fontSize: '1.1rem', color: '#2E7D32', marginBottom: '12px' }}>
              Has your order arrived?
            </h4>
            <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '16px' }}>Please confirm once you have received your order.</p>
            <button onClick={async () => {
              setConfirming(true);
              try {
                await api.put('/orders.php', { order_id: order.order_id });
                setConfirmed(true);
                fetchOrder();
              } catch (err) {
                alert('Failed to confirm delivery. Please try again.');
              } finally {
                setConfirming(false);
              }
            }} disabled={confirming} className="btn btn-green" style={{ padding: '12px 32px', fontSize: '0.9rem', borderRadius: '10px' }}>
              <CheckCircle size={16} style={{ marginRight: '6px' }} /> {confirming ? 'Confirming...' : 'I Have Received My Order'}
            </button>
          </div>
        )}
        {confirmed && !isDelivered && (
          <div style={{ backgroundColor: '#E8F5E9', borderRadius: '16px', padding: '24px', marginBottom: '20px', border: '1px solid #C8E6C9', textAlign: 'center' }}>
            <CheckCircle size={40} color="#4CAF50" style={{ marginBottom: '12px' }} />
            <h4 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 600, fontSize: '1.1rem', color: '#2E7D32', marginBottom: '8px' }}>
              Delivery Confirmed
            </h4>
            <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '16px' }}>Thank you! Enjoy your meal.</p>
          </div>
        )}

        {/* Order details */}
        <div style={{ backgroundColor: '#fff', borderRadius: '16px', padding: '24px', border: '1px solid #EAEDF1' }}>
          <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.1rem', fontWeight: 600, color: '#1A1A1A', marginBottom: '16px' }}>
            Order Details
          </h3>
          {order.items?.map((item, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f0f0f0', fontSize: '0.9rem' }}>
              <span style={{ color: '#555' }}>{item.item_name} ×{item.quantity}</span>
              <span style={{ fontWeight: 600, color: '#333' }}>UGX {Number(item.price * item.quantity).toLocaleString()}</span>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px', paddingTop: '16px', borderTop: '2px solid #e0e0e0' }}>
            <span style={{ fontWeight: 700, color: '#1A1A1A', fontSize: '1.1rem' }}>Total</span>
            <span style={{ fontWeight: 700, color: '#D4AF37', fontSize: '1.2rem' }}>UGX {Number(order.total_amount).toLocaleString()}</span>
          </div>
          <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', fontSize: '0.85rem', color: '#666' }}>
            <span><strong>Payment:</strong> {order.payment_method}</span>
            <span><strong>Order #:</strong> #{order.order_id}</span>
            <span><strong>Date:</strong> {new Date(order.order_date).toLocaleDateString()}</span>
          </div>
        </div>

        {/* Status history */}
        {order.history && order.history.length > 0 && (
          <div style={{ backgroundColor: '#fff', borderRadius: '16px', padding: '24px', marginTop: '20px', border: '1px solid #EAEDF1' }}>
            <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.1rem', fontWeight: 600, color: '#1A1A1A', marginBottom: '16px' }}>
              Status History
            </h3>
            {order.history.map((h, i) => (
              <div key={i} style={{ display: 'flex', gap: '12px', padding: '10px 0', borderBottom: i < order.history.length - 1 ? '1px solid #f5f5f5' : 'none' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: STATUS_COLORS[h.new_status] || '#999', marginTop: '6px', flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#333' }}>
                    {h.old_status ? `${STATUS_LABELS[h.old_status] || h.old_status} → ` : ''}{STATUS_LABELS[h.new_status] || h.new_status}
                  </div>
                  {h.delay_reason && <div style={{ fontSize: '0.78rem', color: '#E65100', marginTop: '2px' }}>{h.delay_reason}</div>}
                  <div style={{ fontSize: '0.72rem', color: '#999', marginTop: '2px' }}>
                    {new Date(h.changed_at).toLocaleString()} {h.eta_minutes ? `• ETA: ${h.eta_minutes} min` : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default OrderTracking;
